import { Box, Tab, Tabs, Typography } from '@mui/material'
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom'
import { CollectionWizard } from './collection/CollectionWizard'
import { FormCompletions } from './FormCompletions'
import { InviteeList } from './invitation/InviteeList'
import { Dashboard } from './dashboard/Dashboard'
import { Management } from './management/Management'
import { useAuth } from '../../../hooks/use-auth'
import { NotAllowed } from '../../masterComponents/ProtectedRoute'
import {
  type P2PSurface,
  P2P_SURFACE_LABEL,
  P2P_SURFACE_SEGMENT,
  canManageCompletions,
  getEntitledSurfaces,
  p2pSurfaceUrl
} from './surfaceRouting'

const SurfaceTabs = ({ entitled }: { entitled: P2PSurface[] }): JSX.Element => {
  const location = useLocation()
  const navigate = useNavigate()
  const current = entitled.find(s => location.pathname.endsWith(`/${P2P_SURFACE_SEGMENT[s]}`)) ?? entitled[0]

  return (
    <Tabs
      value={current}
      onChange={(_, value: P2PSurface) => { navigate(p2pSurfaceUrl(value)) }}
      data-testid='p2pSurfaceTabs'
      sx={{
        mb: '24px',
        borderBottom: '1px solid var(--grey-400)',
        '& .MuiTab-root': { color: 'var(--grey-900)' },
        '& .MuiTab-root.Mui-selected': { color: 'var(--blue-500)' }
      }}
    >
      {entitled.map(surface => (
        <Tab key={surface} label={P2P_SURFACE_LABEL[surface]} value={surface} data-testid={`p2p-${surface}-tab`} />
      ))}
    </Tabs>
  )
}

export const PeerToPeer = (): JSX.Element => {
  const { user } = useAuth()

  if (user == null) return <NotAllowed />

  const entitled = getEntitledSurfaces(user)
  if (entitled.length === 0) return <NotAllowed />

  const completionsReadOnly = !canManageCompletions(user)

  const surfaceElement = (surface: P2PSurface): JSX.Element => {
    switch (surface) {
      case 'collection': return <CollectionWizard />
      case 'completions': return <FormCompletions readOnly={completionsReadOnly} />
      case 'invitations': return <InviteeList />
      case 'dashboard': return <Dashboard />
      case 'statewide': return <Management />
    }
  }

  const defaultUrl = p2pSurfaceUrl(entitled[0])

  const surfaceRoutes = (
    <Routes>
      <Route index element={<Navigate replace to={defaultUrl} />} />
      {entitled.map(surface => (
        <Route key={surface} path={P2P_SURFACE_SEGMENT[surface]} element={surfaceElement(surface)} />
      ))}
      <Route path='*' element={<Navigate replace to={defaultUrl} />} />
    </Routes>
  )

  if (entitled.length === 1) return surfaceRoutes

  return (
    <Box>
      <Typography variant='h1' data-testid='pageHeader' mb='12px'>Peer to Peer</Typography>
      <SurfaceTabs entitled={entitled} />
      {surfaceRoutes}
    </Box>
  )
}
